/**
 * Read marks — task a60d4d9a, stage 2 of the new interface (agreed 08.10, Kostya: «Да,
 * делайте так»). The server keeps one «прочитано до» per person and discussion, so a phone
 * and a computer show the same; a Telegram notification is not «read». An open discussion
 * moves the mark to the newest message it loaded; writing in it moves it too.
 *
 * New = after the mark and not one's own: neither the person's words nor their agent's
 * (an agent writes as its person), nor a notice about an edit they made themselves. The
 * same rule runs on the server (packages/core/services/read_marks.py) for the counters.
 *
 * No runtime imports: the screens' tests load this module as written.
 */
import type { ChatMessage } from "@/api/chat";

export interface ReadMark {
  parent_type?: string;
  parent_id?: string;
  last_read_at: string;
  /** None: nothing read here yet; what came after `last_read_at` (joining) is new. */
  last_read_id: string | null;
}

type Placed = Pick<ChatMessage, "id" | "created_at">;
type Authored = Pick<ChatMessage, "author_id" | "kind" | "meta">;

/**
 * A server time to the microsecond, comparable as text: «…:04Z» and «…:04.198562Z» both
 * become «…:04.000000Z» / «…:04.198562Z» (a whole second drops its fraction in JSON).
 */
export function stamp(iso: string): string {
  const fraction = /\.(\d+)/.exec(iso)?.[1] ?? "";
  const ms = Date.parse(iso.replace(/\.(\d{3})\d+/, ".$1"));
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, `.${fraction.padEnd(6, "0").slice(0, 6)}Z`);
}

/** After the mark by the feed's order: time, then id. */
export function isAfter(message: Placed, mark: ReadMark): boolean {
  const at = stamp(message.created_at);
  const markAt = stamp(mark.last_read_at);
  if (at !== markAt) return at > markAt;
  return mark.last_read_id !== null && message.id > mark.last_read_id;
}

/** One's own words, one's agent's, or a notice about one's own edit. Unknown reader: all. */
export function isOwn(message: Authored, me: string | undefined): boolean {
  if (!me) return true;
  if (message.author_id === me) return true;
  return (message.kind ?? "message") !== "message" && message.meta?.editor_id === me;
}

export function isNew(message: Placed & Authored, mark: ReadMark | null | undefined, me: string | undefined): boolean {
  return mark ? !isOwn(message, me) && isAfter(message, mark) : false;
}

function byFeed(a: Placed, b: Placed): number {
  const x = stamp(a.created_at);
  const y = stamp(b.created_at);
  if (x !== y) return x < y ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** The first new message of what is loaded — the «Новое» line goes at it. */
export function firstNewId(messages: (Placed & Authored)[], mark: ReadMark | null | undefined, me: string | undefined): string | undefined {
  if (!mark || !me) return undefined;
  return [...messages].sort(byFeed).find((m) => isNew(m, mark, me))?.id;
}

/** The message an open discussion marks as read: the newest loaded, if it is past the mark. */
export function markTarget(messages: Placed[], mark: ReadMark | null | undefined): string | undefined {
  const newest = [...messages].sort(byFeed).at(-1);
  if (!newest) return undefined;
  if (mark && !isAfter(newest, mark)) return undefined;
  return newest.id;
}

/**
 * Where the line stands in a feed in reading order. Oldest first: right before the first
 * new message. Newest first: right after it, so everything above the line is new.
 */
export function withNewLine<T extends { id: string }>(items: T[], firstNew: string | undefined, newestFirst: boolean, line: T): T[] {
  const at = firstNew ? items.findIndex((item) => item.id === firstNew) : -1;
  if (at < 0) return items;
  const place = newestFirst ? at + 1 : at;
  return [...items.slice(0, place), line, ...items.slice(place)];
}

/** What a counter shows: up to 99, then «99+». */
export function countLabel(n: number): string {
  return n > 99 ? "99+" : String(n);
}
